import { abilityLabel } from './labels.js';
import { schoolShort } from './schools.js';
import { skillName } from './skills.js';
import type {
  CandidateProfile,
  MatchOptions,
  MatchResult,
  MatchWeights,
  Requirement,
  ScoreBreakdown,
  TeamRequest,
} from './types.js';
import { DEFAULT_WEIGHTS, assertWeights } from './weights.js';

/**
 * 匹配引擎 v1.2「项目需求驱动」。
 *
 * score = 100 × ( 0.40·需求覆盖 + 0.25·团队互补 + 0.20·时间协同 + 0.15·跨校 )
 *
 * 与旧版（v1.1 线性加权 + Math.min(1) 截断）的关键差异：
 * 1. **需求覆盖是分母固定的比例**：Σ命中权重 / Σ需求权重 —— 候选人"技能越杂"不再自动涨分；
 * 2. **不再有长板/短板直接加分**：避免与互补度重复计分；
 * 3. 输出**池内百分位**，界面不得称其为"匹配准确率/概率"。
 *
 * 该函数是纯函数：不读环境、不写日志、不碰网络，便于单测与复用（Web/后端/脚本同一份实现）。
 */
export function rankCandidates(
  request: TeamRequest,
  candidates: CandidateProfile[],
  options: MatchOptions = {},
): MatchResult[] {
  const weights: MatchWeights = { ...DEFAULT_WEIGHTS, ...(options.weights ?? {}) };
  assertWeights(weights);

  const requirements = normalizeRequirements(request);
  const totalWeight = requirements.reduce((sum, r) => sum + r.weight, 0);
  const teamSkills = request.teamSkills ?? [];
  const teamSchools = request.teamSchools ?? [];

  const scored = candidates.map((candidate) =>
    scoreCandidate(candidate, requirements, totalWeight, teamSkills, teamSchools, request, weights, options),
  );

  // 池内百分位：本次候选池中不高于该分数的比例（0–100）
  const totals = scored.map((s) => s.total).sort((a, b) => a - b);
  for (const item of scored) {
    const notHigher = totals.filter((t) => t <= item.total).length;
    item.percentile = totals.length === 0 ? 0 : Math.round((notHigher / totals.length) * 100);
  }

  // 排序：分数降序 → 姓名 → id（保证同分时结果稳定、可复现）
  scored.sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'zh') || a.userId.localeCompare(b.userId));
  return typeof options.limit === 'number' ? scored.slice(0, options.limit) : scored;
}

/** 兼容两种写法：requirements（带权重）优先，否则把 requiredSkills 当等权需求 */
export function normalizeRequirements(request: TeamRequest): Required<Requirement>[] {
  if (request.requirements && request.requirements.length > 0) {
    return request.requirements.map((r) => ({
      skillId: r.skillId,
      weight: r.weight && r.weight > 0 ? r.weight : 1,
      minLevel: r.minLevel ?? 2,
    }));
  }
  return (request.requiredSkills ?? []).map((skillId) => ({ skillId, weight: 1, minLevel: 2 as const }));
}

function scoreCandidate(
  candidate: CandidateProfile,
  requirements: Required<Requirement>[],
  totalWeight: number,
  teamSkills: string[],
  teamSchools: string[],
  request: TeamRequest,
  weights: MatchWeights,
  options: MatchOptions,
): MatchResult {
  const candidateSkills = new Set(candidate.skills);
  const covered: string[] = [];
  const missing: string[] = [];
  let coverageWeight = 0;

  for (const req of requirements) {
    const has = candidateSkills.has(req.skillId);
    const level = candidate.skillLevels?.[req.skillId] ?? (has ? 2 : 0);
    if (level >= req.minLevel) {
      coverageWeight += req.weight;
      covered.push(req.skillId);
    } else if (level > 0) {
      coverageWeight += req.weight * 0.5; // 会但不够熟练：半分
      covered.push(req.skillId);
    } else {
      missing.push(req.skillId);
    }
  }

  const coverage = totalWeight > 0 ? clamp01(coverageWeight / totalWeight) : 0;
  const complement = computeComplement(candidate, teamSkills, options);
  const time = computeTimeScore(candidate, request);
  const crossSchool = computeCrossSchoolScore(candidate, teamSchools, request);

  const total = round1(
    100 * (weights.coverage * coverage + weights.complement * complement + weights.time * time + weights.crossSchool * crossSchool),
  );

  const breakdown: ScoreBreakdown = {
    coverage: round3(coverage),
    complement: round3(complement),
    time: round3(time),
    crossSchool: round3(crossSchool),
  };

  return {
    userId: candidate.userId,
    name: candidate.name,
    school: candidate.school,
    total,
    breakdown,
    percentile: 0,
    reasons: buildReasons(candidate, { coverage, complement, time, crossSchool }, covered, missing, requirements.length, request),
    missing,
    covered,
  };
}

/** 团队互补 = 1 − 余弦相似度（技能集合向量） */
function computeComplement(candidate: CandidateProfile, teamSkills: string[], options: MatchOptions): number {
  if (teamSkills.length === 0) return options.emptyTeamComplement ?? 0.5;
  if (candidate.skills.length === 0) return 0;
  const team = new Set(teamSkills);
  const shared = candidate.skills.filter((s) => team.has(s)).length;
  const cosine = shared / Math.sqrt(candidate.skills.length * team.size);
  return clamp01(1 - cosine);
}

/** 时间协同：优先按时段 Jaccard，其次按周小时数接近度，都没有则中性 0.5 */
function computeTimeScore(candidate: CandidateProfile, request: TeamRequest): number {
  const needSlots = request.slots ?? [];
  const candSlots = candidate.slots ?? [];
  if (needSlots.length > 0 && candSlots.length > 0) {
    const need = new Set(needSlots);
    const inter = candSlots.filter((s) => need.has(s)).length;
    const union = new Set([...needSlots, ...candSlots]).size;
    return union === 0 ? 0.5 : clamp01(inter / union);
  }
  const need = request.weeklyHoursNeeded ?? 0;
  if (need > 0) return clamp01(1 - Math.min(1, Math.abs(candidate.weeklyHours - need) / need));
  return 0.5;
}

/** 跨校分：同校 0.4 / 跨校 1 / 未启用跨校 0 / 队伍尚无成员 0.7（中性） */
function computeCrossSchoolScore(candidate: CandidateProfile, teamSchools: string[], request: TeamRequest): number {
  if (request.allowCrossSchool === false) return 0;
  if (teamSchools.length === 0) return 0.7;
  return teamSchools.includes(candidate.school) ? 0.4 : 1;
}

/** 生成"能当面讲清楚"的理由 */
function buildReasons(
  candidate: CandidateProfile,
  parts: { coverage: number; complement: number; time: number; crossSchool: number },
  covered: string[],
  missing: string[],
  requirementCount: number,
  request: TeamRequest,
): string[] {
  const reasons: string[] = [];

  if (requirementCount === 0) {
    reasons.push('项目还没填技能需求，本次只能按团队互补与时间估算，建议先补充需求');
  } else if (parts.coverage >= 0.8) {
    reasons.push(`需求覆盖 ${covered.length}/${requirementCount}：${covered.map(skillName).join('、')}`);
  } else if (parts.coverage > 0) {
    reasons.push(
      `需求覆盖 ${covered.length}/${requirementCount}（命中 ${covered.map(skillName).join('、')}；缺 ${missing
        .map(skillName)
        .join('、')}）`,
    );
  } else {
    reasons.push(`技能未覆盖项目需求（缺 ${missing.map(skillName).join('、')}）`);
  }

  if (parts.complement >= 0.7) reasons.push('与现有队伍技能重合度低，互补性强');
  else if (parts.complement <= 0.3) reasons.push('技能与现有队伍高度重合，互补作用有限');

  if (parts.time >= 0.8) reasons.push(`每周可投入 ${candidate.weeklyHours} 小时，与需求匹配`);
  else if (parts.time <= 0.4) reasons.push(`每周可投入 ${candidate.weeklyHours} 小时，与需求差距较大`);

  if (request.allowCrossSchool === false) reasons.push('本次未启用跨校匹配');
  else if (parts.crossSchool >= 1) reasons.push(`跨校（${schoolShort(candidate.school)}），带来跨校视角`);
  else if (parts.crossSchool <= 0.4 && (request.teamSchools?.length ?? 0) > 0) reasons.push('与队伍同校，沟通成本更低');

  if (candidate.weaknesses?.length) {
    reasons.push(`自评短板：${candidate.weaknesses.map((d) => abilityLabel(d)).join('、')}（可由队友补位）`);
  }
  return reasons;
}

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(1, n));
}
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** 分数说明（用于界面浮层与答辩口径） */
export function explainScore(result: MatchResult, weights: MatchWeights = DEFAULT_WEIGHTS): string {
  const b = result.breakdown;
  return (
    `需求覆盖 ${Math.round(b.coverage * 100)}%×${Math.round(weights.coverage * 100)}% ＋ ` +
    `团队互补 ${Math.round(b.complement * 100)}%×${Math.round(weights.complement * 100)}% ＋ ` +
    `时间协同 ${Math.round(b.time * 100)}%×${Math.round(weights.time * 100)}% ＋ ` +
    `跨校 ${Math.round(b.crossSchool * 100)}%×${Math.round(weights.crossSchool * 100)}% ` +
    `＝ ${result.total}（池内百分位 ${result.percentile}，排序分，非准确率）`
  );
}